import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { chain } from "lodash";
import type { FC, ReactNode } from "react";
import type { Control } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import CurrencyPicker from "../../components/currencyPicker/CurrencyPicker";
import FormCheckbox from "../../components/formComponents/FormCheckbox";
import FormInfoEdit from "../../components/formComponents/FormInfoEdit";
import FormNumberField from "../../components/formComponents/FormNumberField";
import FormTextField from "../../components/formComponents/FormTextField";
import ImagesPicker from "../../components/imagesPicker/ImagesPicker";
import { Select } from "../../components/select/Select";
import TagSelect from "../../components/tagSelect/TagSelect";
import type { Currency } from "../../data/BaseEntity";
import { useImages } from "../../media/useImages";
import { useTripTagsByCategory } from "../../orders/useTripTagsByCategory";
import type { ImageFlavour } from "../../publicPackages/publicPackagesApi";
import PackageSelect from "../../sharedComponents/packages/PackageSelect";
import { useRegions } from "../dictionaries/useRegions";
import PackageDays from "./PackageDays";
import type { PackageDayDescriptionType } from "./PackageDescriptionTypes";
import { packageDescriptionTypes } from "./PackageDescriptionTypes";
import type { IdValidationResult } from "./packageUserFriendlyId";

interface PackageImage {
  readonly id: string;
  readonly name: string;
  readonly thumbnail: { url: string };
  readonly original: { url: string };
  readonly www: { readonly url: string };
  readonly flavours: Partial<Record<ImageFlavour, { readonly url: string }>>;
}

interface Tag {
  readonly category: string;
  readonly value: string;
}

export interface Package {
  userFriendlyId: string;
  name: string;
  wwwName?: string;
  note: string | undefined;
  description?: string;
  margin: number;
  currency: Currency;
  detailsDict: Record<string, string | null>;
  days: {
    dayNumber: number;
    title: string;
    details: { description?: string; plan?: string };
    products: { id: string; optional: boolean; options: (string | null)[] }[];
    image?: PackageImage;
  }[];
  tags: Tag[];
  map?: PackageImage;
  images: PackageImage[];
  isPublic: boolean;
  isPublicDayDesc: Partial<Record<PackageDayDescriptionType, boolean>>;
  regionId?: string | null;
  parentPackageId?: string | null;
}

interface Props {
  readonly control: Control<Package>;
  readonly errors: string[];
  readonly attachments?: ReactNode;
  readonly parentPackageId?: string;
  readonly validateId: (value: string) => IdValidationResult;
  readonly userFriendlyIdPrefix: string;
}

const PackageEdit: FC<Props> = ({
  control,
  errors,
  attachments,
  parentPackageId,
  validateId,
  userFriendlyIdPrefix,
}) => {
  const { images, isLoading } = useImages();
  const { tripTagsByCategory } = useTripTagsByCategory();
  const { regions } = useRegions();
  const days = useWatch({ control, name: "days" }) ?? [];
  const dayProductIds = days.flatMap((day) => (day?.products ?? []).map((product) => product.id));

  return (
    <Grid size={12} container spacing={2}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="userFriendlyId"
          control={control}
          rules={{
            validate: (value) => {
              const result = validateId(value ?? "");

              if (result.valid) return true;

              switch (result.error) {
                case "required":
                  return "ID jest wymagane";
                case "prefix":
                  return `ID musi zaczynać się od ${userFriendlyIdPrefix}`;
                case "duplicate":
                  return "Takie ID już istnieje";
                default:
                  return "Nieprawidłowy format ID";
              }
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="ID"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              value={
                field.value?.startsWith(userFriendlyIdPrefix)
                  ? field.value.slice(userFriendlyIdPrefix.length)
                  : field.value
              }
              onChange={(event) => {
                field.onChange(`${userFriendlyIdPrefix}${event.target.value}`);
              }}
              slotProps={{
                input: {
                  startAdornment: userFriendlyIdPrefix ? (
                    <InputAdornment position="start">{userFriendlyIdPrefix}</InputAdornment>
                  ) : undefined,
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <FormTextField label="Nazwa" name="name" autoFocus control={control} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <FormTextField label="Nazwa WWW" name="wwwName" autoFocus control={control} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="regionId"
          control={control}
          rules={{ required: "Region jest wymagany" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              fullWidth
              label="Region"
              values={regions.map((r) => ({ value: r.id.id, label: r.name }))}
              value={value ?? ""}
              onSelect={onChange}
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="parentPackageId"
          control={control}
          render={({ field: { onChange, value } }) => (
            <PackageSelect
              label="Pakiet nadrzędny"
              packageId={value ?? undefined}
              onChange={(p) => onChange(p?.id.id)}
              exceptPackageId={parentPackageId}
              fullWidth
              parentOnly={true}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 4, lg: 2 }}>
        <FormCheckbox label="Pakiet publiczny" name="isPublic" control={control} />
      </Grid>
      <Grid
        size={{ xs: 4, lg: 2 }}
        sx={{
          justifyContent: "center",
        }}
      >
        <Controller
          name="currency"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl>
              <FormLabel>Waluta</FormLabel>
              <CurrencyPicker value={value} onChange={onChange} />
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 4, lg: 2 }}>
        <FormNumberField label="Marża" name="margin" control={control} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="isPublicDayDesc"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl component="fieldset" variant="standard" fullWidth>
              <FormLabel component="legend">Publiczne opisy dni</FormLabel>
              <FormGroup row>
                {chain(packageDescriptionTypes)
                  .entries()
                  .map(([descType, label]) => (
                    <FormControlLabel
                      key={descType}
                      control={
                        <Checkbox
                          checked={value[descType as PackageDayDescriptionType]}
                          onChange={(event, checked) =>
                            onChange({
                              ...value,
                              [descType]: checked,
                            })
                          }
                          name={`${descType}.isPublic`}
                        />
                      }
                      label={label}
                    />
                  ))
                  .value()}
              </FormGroup>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="tags"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TagSelect tags={value ?? []} availableTags={tripTagsByCategory} onChange={onChange} />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Controller
          name="detailsDict"
          control={control}
          render={() => (
            <FormControl component="fieldset" variant="standard" fullWidth>
              <FormLabel component="legend">Nazwy opisów</FormLabel>
              <FormGroup
                row
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 1,
                }}
              >
                {chain(packageDescriptionTypes)
                  .entries()
                  .map(([descType, label]) => (
                    <FormTextField
                      key={descType}
                      label={label}
                      name={`detailsDict.${descType as PackageDayDescriptionType}`}
                      control={control}
                    />
                  ))
                  .value()}
              </FormGroup>
            </FormControl>
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <FormInfoEdit label="Dodatkowe informacje" name="note" control={control} />
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }}>
        <FormInfoEdit label="Opis WWW" name="description" control={control} />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <Controller
          name="map"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ImagesPicker
              label="Mapa"
              limit={1}
              images={images}
              isLoading={isLoading}
              selectedImages={value ? [value] : []}
              onSelectionChange={(images) => onChange(images.length === 0 ? undefined : images[0])}
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 10 }}>
        <Controller
          name="images"
          control={control}
          render={({ field: { onChange, value } }) => (
            <ImagesPicker
              label="Zdjęcia"
              limit={10}
              images={images}
              isLoading={isLoading}
              selectedImages={value ?? []}
              onSelectionChange={onChange}
              productIds={dayProductIds}
            />
          )}
        />
      </Grid>
      {attachments && (
        <Grid size={12}>
          <FormControl>
            <FormLabel>Powiązane pliki</FormLabel>
            {attachments}
          </FormControl>
        </Grid>
      )}
      <Grid size={12}>
        <Controller
          name="days"
          control={control}
          render={({ field: { onChange, value } }) => <PackageDays days={value ?? []} onChange={onChange} />}
        />
      </Grid>
      <Grid size={12}>
        {errors.length > 0 && (
          <Typography variant="body2" color="error">
            Nie wszystkie produkty mają ceny w walucie zgodej z wybraną dla pakietu
          </Typography>
        )}
      </Grid>
    </Grid>
  );
};

export default PackageEdit;
